import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate, formatEuro } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { StatutAvoirBadge, TypeAvoirBadge } from '../components/tresorerie/StatutAvoirBadge';
import type { StatutAvoir } from '@cockpit/shared';

export function TresorerieAvoirsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [statut, setStatut] = useState<string>('');
  const [type, setType] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['avoirs', { statut, type }],
    queryFn: () =>
      api.avoirs.list({
        ...(statut === '' ? {} : { statut }),
        ...(type === '' ? {} : { type_avoir: type }),
      }),
  });

  const changerStatut = useMutation({
    mutationFn: ({ id, nouveau }: { id: number; nouveau: Exclude<StatutAvoir, 'Emis'> }) =>
      api.avoirs.changerStatut(id, { nouveau_statut: nouveau }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['avoirs'] });
      void queryClient.invalidateQueries({ queryKey: ['encaissements'] });
    },
  });

  return (
    <div className="max-w-7xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">Avoirs</h1>
        <p className="text-xs text-slate-500 mt-1">
          Avoirs Crédit (utilisables sur prochaine commande) ou Remboursement (sortie cash).
        </p>
      </header>

      <div className="flex gap-3">
        <Select
          label="Type"
          value={type}
          onChange={(e): void => setType(e.target.value)}
          options={[
            { value: '', label: 'Tous' },
            { value: 'Credit', label: 'Crédit' },
            { value: 'Remboursement', label: 'Remboursement' },
          ]}
        />
        <Select
          label="Statut"
          value={statut}
          onChange={(e): void => setStatut(e.target.value)}
          options={[
            { value: '', label: 'Tous' },
            { value: 'Emis', label: 'Émis' },
            { value: 'Utilise', label: 'Utilisé' },
            { value: 'Rembourse', label: 'Remboursé' },
            { value: 'Annule', label: 'Annulé' },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/50 border-b border-slate-800">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">N°</th>
                <th className="py-2 px-3">Vente</th>
                <th className="py-2 px-3">Client</th>
                <th className="py-2 px-3">Type</th>
                <th className="py-2 px-3">Statut</th>
                <th className="py-2 px-3 text-right">TTC</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((a) => (
                <tr key={a.id} className="border-b border-slate-800/50">
                  <td className="py-2 px-3">{formatDate(a.date_emission)}</td>
                  <td className="py-2 px-3">{a.numero_avoir_henrri ?? `#${String(a.id)}`}</td>
                  <td className="py-2 px-3">
                    <Link
                      to={`/ventes/${String(a.vente_id)}`}
                      className="text-floryon-green hover:underline"
                    >
                      {a.vente_numero_henrri ?? `#${String(a.vente_id)}`}
                    </Link>
                  </td>
                  <td className="py-2 px-3">{a.contact_societe ?? a.contact_nom}</td>
                  <td className="py-2 px-3">
                    <TypeAvoirBadge type={a.type_avoir} />
                  </td>
                  <td className="py-2 px-3">
                    <StatutAvoirBadge statut={a.statut} />
                  </td>
                  <td className="py-2 px-3 text-right">{formatEuro(a.montant_ttc_centimes)}</td>
                  <td className="py-2 px-3 text-right">
                    {a.statut === 'Emis' && (
                      <div className="flex gap-1 justify-end">
                        {a.type_avoir === 'Credit' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(): void =>
                              changerStatut.mutate({ id: a.id, nouveau: 'Utilise' })
                            }
                          >
                            Marquer utilisé
                          </Button>
                        )}
                        {a.type_avoir === 'Remboursement' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(): void =>
                              changerStatut.mutate({ id: a.id, nouveau: 'Rembourse' })
                            }
                          >
                            Marquer remboursé
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(): void =>
                            changerStatut.mutate({ id: a.id, nouveau: 'Annule' })
                          }
                        >
                          Annuler
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {data?.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-sm text-slate-500">
                    Aucun avoir.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
